/**
 * URL 파라미터 업데이트 유틸리티 함수들
 */

/**
 * 기존 URL 파라미터를 유지하면서 새로운 파라미터를 추가/업데이트하거나 삭제합니다.
 * @param currentSearchParams - 현재 URLSearchParams 또는 문자열
 * @param updates - 업데이트할 파라미터 객체 (null 값은 삭제)
 * @returns 업데이트된 URLSearchParams 객체
 */
export const updateUrlParams = (
  currentSearchParams: URLSearchParams | string | null,
  updates: Record<string, string | number | null | undefined>,
): URLSearchParams => {
  // 현재 파라미터를 기반으로 새로운 URLSearchParams 생성
  const newParams = new URLSearchParams(currentSearchParams?.toString() ?? '');

  // 업데이트 적용
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      // null이나 undefined인 경우 해당 키 삭제
      newParams.delete(key);
    } else {
      // 값이 있는 경우 설정 (문자열로 변환)
      newParams.set(key, String(value));
    }
  });

  return newParams;
};

/**
 * 현재 URL 파라미터를 객체 형태로 반환합니다.
 * @param searchParams - URLSearchParams 객체
 * @returns 파라미터 객체
 */
export const searchParamsToObject = (
  searchParams: URLSearchParams | null,
): Record<string, string> => {
  const params: Record<string, string> = {};

  if (searchParams) {
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
  }

  return params;
};

/**
 * 기존 파라미터를 유지하면서 새로운 경로로 이동할 수 있는 URL을 생성합니다.
 * @param basePath - 기본 경로
 * @param currentSearchParams - 현재 URLSearchParams
 * @param updates - 업데이트할 파라미터
 * @returns 완성된 URL 문자열
 */
export const buildUrlWithParams = (
  basePath: string,
  currentSearchParams: URLSearchParams | string | null,
  updates?: Record<string, string | number | null | undefined>,
): string => {
  const params = updates
    ? updateUrlParams(currentSearchParams, updates)
    : new URLSearchParams(currentSearchParams?.toString() ?? '');

  const queryString = params.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

/**
 * 광고 attribution 파라미터 allowlist.
 *
 * 랜딩에서 로그인/가입 페이지로 넘어갈 때 이 목록만 따라간다. 'all' 로 전부 넘기면
 * returnUrl 처럼 목적지 동작을 바꾸는 값까지 섞이므로, 유입 출처 추적에 필요한 키만 둔다.
 */
export const ATTRIBUTION_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'igshid',
  'atTrackId',
] as const;

/**
 * 파라미터 보존 모드
 * - `atTrackId`: atTrackId 만 (기본값)
 * - `attribution`: 광고 attribution allowlist ([ATTRIBUTION_PARAM_KEYS])
 * - `all`: 모든 파라미터
 */
export type PreserveParamsMode = 'atTrackId' | 'attribution' | 'all';

/**
 * 현재 URL에서 보존할 파라미터만 추출합니다.
 * @param searchParams - 현재 URLSearchParams
 * @param mode - 보존 모드
 * @returns 추출된 파라미터 객체
 */
export const extractPreservedParams = (
  searchParams: URLSearchParams | null,
  mode: PreserveParamsMode = 'atTrackId',
): Record<string, string> => {
  const params: Record<string, string> = {};

  if (!searchParams) {
    return params;
  }

  if (mode === 'atTrackId') {
    // atTrackId만 추출
    const atTrackId = searchParams.get('atTrackId');
    if (atTrackId) {
      params.atTrackId = atTrackId;
    }
  } else if (mode === 'attribution') {
    // 광고 attribution allowlist 만 추출
    ATTRIBUTION_PARAM_KEYS.forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    });
  } else if (mode === 'all') {
    // 모든 파라미터 추출
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
  }

  return params;
};

/**
 * href에 보존된 파라미터를 병합합니다.
 * 제네릭 타입을 사용하여 입력 타입을 그대로 반환합니다.
 * @param href - 원본 href (상대 경로, 절대 경로, 또는 URL 객체)
 * @param searchParams - 현재 URLSearchParams
 * @param mode - 보존 모드
 * @returns 파라미터가 병합된 href (입력 타입과 동일)
 */
export function mergeHrefWithParams<T>(
  href: T,
  searchParams: URLSearchParams | null,
  mode: PreserveParamsMode = 'atTrackId',
): T {
  // 보존할 파라미터 추출
  const preservedParams = extractPreservedParams(searchParams, mode);

  // 보존할 파라미터가 없으면 원본 반환
  if (Object.keys(preservedParams).length === 0) {
    return href;
  }

  // href가 객체인 경우 (next-intl의 타입)
  if (typeof href === 'object' && href !== null && 'pathname' in href) {
    const hrefObj = href as {
      pathname: string;
      query?: Record<string, string>;
    };
    return {
      pathname: hrefObj.pathname,
      query: {
        ...preservedParams,
        ...(hrefObj.query || {}),
      },
    } as T;
  }

  // 문자열인 경우
  if (typeof href === 'string') {
    const hrefString = href;

    // 외부 링크나 특수 프로토콜은 그대로 반환
    if (
      hrefString.startsWith('http://') ||
      hrefString.startsWith('https://') ||
      hrefString.startsWith('mailto:') ||
      hrefString.startsWith('tel:') ||
      hrefString.startsWith('#')
    ) {
      return hrefString as T;
    }

    // 내부 링크 처리
    const [pathname, existingQuery] = hrefString.split('?');
    const existingParams = new URLSearchParams(existingQuery || '');

    // 기존 파라미터에 보존 파라미터 병합 (기존 파라미터가 우선)
    Object.entries(preservedParams).forEach(([key, value]) => {
      if (!existingParams.has(key)) {
        existingParams.set(key, value);
      }
    });

    const queryString = existingParams.toString();
    return (queryString ? `${pathname}?${queryString}` : pathname) as T;
  }

  // 기타 타입은 그대로 반환
  return href;
}

/**
 * React Hook - 파라미터를 유지하면서 라우터 푸시하는 함수를 반환합니다.
 */

import { useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { useRouter } from '../i18n/navigation';

export const useRouterWithParams = () => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const pushWithParams = useCallback(
    (
      path: string,
      updates?: Record<string, string | number | null | undefined>,
    ) => {
      const url = buildUrlWithParams(path, searchParams, updates);
      router.push(url);
    },
    [router, searchParams],
  );

  const replaceWithParams = useCallback(
    (
      path: string,
      updates?: Record<string, string | number | null | undefined>,
    ) => {
      const url = buildUrlWithParams(path, searchParams, updates);
      router.replace(url);
    },
    [router, searchParams],
  );

  return {
    pushWithParams,
    replaceWithParams,
    searchParams,
  };
};

/**
 * 사용 예시:
 *
 * 1. 기본 사용법 (Hook 사용):
 * ```typescript
 * const { pushWithParams } = useRouterWithParams();
 *
 * // 현재 URL: /my-space?filter=all&sort=latest
 * // 새 페이지로 이동하면서 기존 파라미터 유지하고 새 파라미터 추가
 * pushWithParams('/my-space/123', { type: 'video' });
 * // 결과: /my-space/123?filter=all&sort=latest&type=video
 *
 * // 특정 파라미터만 업데이트
 * pushWithParams('/my-space', { filter: 'project', type: null });
 * // 결과: /my-space?sort=latest&filter=project (type은 삭제됨)
 * ```
 *
 * 2. 직접 함수 사용:
 * ```typescript
 * const searchParams = useSearchParams();
 * const router = useRouter();
 *
 * const handleNavigate = () => {
 *   const url = buildUrlWithParams('/my-space', searchParams, { filter: 'asset' });
 *   router.push(url);
 * };
 * ```
 *
 * 3. 기존 MySpaceFilterView 리팩토링 예시:
 * ```typescript
 * // 기존 코드
 * const updateQuery = useCallback(
 *   (updates: Record<string, string | null>) => {
 *     const newQuery = new URLSearchParams(searchParams?.toString() ?? '');
 *     Object.entries(updates).forEach(([key, value]) => {
 *       if (value === null) {
 *         newQuery.delete(key);
 *       } else {
 *         newQuery.set(key, value);
 *       }
 *     });
 *     router.push(`/my-space${id ? `/${id}` : ''}?${newQuery.toString()}`);
 *   },
 *   [router, id, searchParams],
 * );
 *
 * // 개선된 코드
 * const { pushWithParams } = useRouterWithParams();
 *
 * const updateQuery = useCallback(
 *   (updates: Record<string, string | null>) => {
 *     pushWithParams(`/my-space${id ? `/${id}` : ''}`, updates);
 *   },
 *   [pushWithParams, id],
 * );
 * ```
 */
